import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, Clock, Tag } from "lucide-react";
import Logo from "../components/Logo";
import { loginWithDocument } from "../lib/api";
import { saveSession, getSession, formatCpfCnpj } from "../lib/auth";
import { toast } from "sonner";

export default function Login() {
  const [doc, setDoc] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // If already logged in, jump straight to home (handles back-to-root after logout edge cases)
  React.useEffect(() => {
    if (getSession()) navigate("/home", { replace: true });
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    const digits = doc.replace(/\D/g, "");
    if (digits.length !== 11 && digits.length !== 14) {
      toast.error("Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.");
      return;
    }
    setLoading(true);
    try {
      const user = await loginWithDocument(digits);
      saveSession(user);
      toast.success(`Bem-vindo${user.name ? `, ${user.name.split(" ")[0]}` : ""}!`);
      navigate("/home", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Falha ao validar acesso.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell relative overflow-hidden" data-testid="login-screen">
      <div className="login-blob" style={{ top: -80, right: -80 }} />
      <div className="login-blob" style={{ bottom: -100, left: -100, opacity: 0.6 }} />

      <div className="relative z-10 px-6 pt-16 pb-10 flex flex-col gap-10 min-h-screen">
        <div className="fade-in">
          <Logo size="lg" />
          <h1 className="mt-8 font-display text-3xl font-bold text-slate-900 dark:text-white leading-tight">
            Assistência <span className="text-[#00D285]">automotiva</span> <br />
            na palma da sua mão.
          </h1>

          <p className="mt-3 text-slate-500 dark:text-slate-400 text-sm">
            Acesse com seu CPF ou CNPJ cadastrado para encontrar serviços emergenciais perto de você.
          </p>
        </div>

        <form onSubmit={submit} className="flex flex-col gap-4 fade-in" data-testid="login-form">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            CPF ou CNPJ
          </label>
          <input
            data-testid="login-document-input"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={doc}
            onChange={(e) => setDoc(formatCpfCnpj(e.target.value))}
            placeholder="000.000.000-00"
            className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 dark:text-white rounded-xl px-4 py-4 text-lg font-medium tracking-wide outline-none focus:border-[#00D285] dark:focus:border-[#00D285] focus:ring-4 focus:ring-[#00D285]/15 transition"
          />
          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="btn-press w-full bg-[#00D285] hover:bg-[#00B874] disabled:opacity-60 text-white font-semibold py-4 rounded-xl shadow-sm flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={20} /> : null}
            {loading ? "Validando..." : "Entrar"}
          </button>
          <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
            Acesso liberado apenas para clientes ativos GoProtege.
          </p>
        </form>

        <div className="space-y-3 fade-in mt-auto">
          <Feature icon={ShieldCheck} title="Localize prestadores próximos a você" />
          <Feature icon={Clock} title="Atendimento rápido de serviços emergenciais" />
          <Feature icon={Tag} title="Condições exclusivas para clientes GoProtege" />
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg bg-[#00D285]/12 flex items-center justify-center">
        <Icon size={18} className="text-[#00D285]" />
      </div>
      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{title}</span>
    </div>
  );
}
